<template>
  <section class="content-block">
    <div class="max-w-[1430px] m-auto px-4 py-8">
      <div class="space-y-8">
        <!-- Breadcrumb -->
        <SharedBreadcrumb :items="breadcrumbItems" />

        <!-- Title and Content Section -->
        <div class="prose max-w-none">
          <h1 class="text-primary text-[36px] font-[900] mb-6">
            {{ content.title }}
          </h1>
          <div
            v-html="renderedContent"
            class="text-font-primary space-y-4 max-w-[700px]"
          ></div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import MarkdownIt from "markdown-it";
import { computed } from "vue";
import { useMainStore } from "@/stores/main";
import { useRoute } from "vue-router";

const mainStore = useMainStore();
const route = useRoute();

const props = defineProps<{
  content: {
    title: string;
    content: string;
    iframeUrl: string;
    slug?: string;
  };
}>();

const md = new MarkdownIt({
  html: true, // Enable HTML tags for phone number links
  linkify: true, // Enable auto-converting URL-like text to links
  breaks: true, // Convert \n to <br>
});

// Add custom renderer for phone links
md.renderer.rules.link_open = (
  tokens: any,
  idx: any,
  options: any,
  env: any,
  self: any
) => {
  const token = tokens[idx];
  const href = token.attrGet("href");

  if (href && href.startsWith("tel:")) {
    // Format phone number by removing spaces
    const phone = href.replace("tel:", "").replace(/\s+/g, "");
    token.attrSet("href", `tel:${phone}`);
  }

  return self.renderToken(tokens, idx, options);
};

// Find category title from FAQs
const categoryTitle = computed(() => {
  const faqs = mainStore.getFaqs;
  // Get current route path and extract the slug
  const currentPath = route.path;
  const slug = currentPath.split("/").pop();

  const cat = faqs.find((faq) => faq.slug === slug);

  return cat?.faqs_category?.title || "Getting Started & Porting";
});

// Create breadcrumb items
const breadcrumbItems = computed(() => [
  { text: "Help", to: "/faqs" },
  { text: "FAQ", to: "/faqs" },
  { text: categoryTitle.value, to: "/faqs" },
  { text: props.content.title, active: true },
]);

// Convert markdown content to HTML
const renderedContent = computed(() => {
  let cleanContent = props.content.content
    .split("\n")
    .map((line) => line.trim())
    .join("\n");

  // Find and format phone number links
  // Example: Call [02 5126 0000](tel:02 5126 0000)
  cleanContent = cleanContent.replace(
    /\[(\d[\d\s]+)\]\(tel:[\d\s]+\)/g,
    (match, phone) => {
      const cleanPhone = phone.replace(/\s+/g, "");
      return `[${phone}](tel:${cleanPhone})`;
    }
  );

  return md.render(cleanContent);
});
</script>

<style scoped>
.content-block {
  @apply bg-white;
}

:deep(.prose) {
  table {
    @apply w-full border-collapse border border-gray-300;
  }

  th {
    @apply bg-white p-2 text-left border border-gray-300;
  }

  td {
    @apply p-2 border border-gray-300;
  }
  tr {
    @apply border border-[#808080];
  }
  tr:nth-child(odd) td {
    @apply bg-[#ddd];
  }

  tr:nth-child(even) td {
    @apply bg-white;
  }
}
</style>
